import { useState, useCallback, useEffect } from 'react';
import { cashMovementsService } from '../services/cashMovementsService';

/**
 * Hook personalizado para gestión de movimientos de caja
 * Encapsula lógica de estado y operaciones CRUD
 */
export const useCashMovementsManagement = () => {
  // Estados principales
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados de filtros
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    sede: ''
  });

  // Estados de estadísticas
  const [stats, setStats] = useState({
    totalMovements: 0,
    totalIncome: 0,
    totalExpenses: 0,
    netBalance: 0
  });

  // Estado para controlar si las estadísticas están activas (filtros aplicados)
  const [statsActive, setStatsActive] = useState(false);

  // Limpiar errores
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar movimientos de caja
  const fetchCashMovements = useCallback(async (searchFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { ...filters, ...searchFilters };
      
      // Filtrar parámetros vacíos
      const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value && value.toString().trim() !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await cashMovementsService.getCashMovements(cleanParams);
      
      // El backend ahora devuelve { success: true, data: [...], stats: {...} }
      // NOTA: No actualizamos stats aquí, solo movimientos
      if (response.success) {
        setMovements(Array.isArray(response.data) ? response.data : []);
        // NO actualizar stats automáticamente
      } else {
        setMovements(Array.isArray(response.data) ? response.data : []);
        // NO actualizar stats automáticamente
      }
    } catch (err) {
      setError(err.message);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Buscar por rango de fechas
  const searchByDateRange = useCallback(async (startDate, endDate, sede = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await cashMovementsService.getCashMovementsByDateRange(startDate, endDate, sede);
      
      // El backend ahora devuelve { success: true, data: [...], stats: {...} }
      if (response.success) {
        setMovements(Array.isArray(response.data) ? response.data : []);
        if (response.stats) {
          setStats(response.stats);
        }
      } else {
        setMovements(Array.isArray(response.data) ? response.data : []);
        if (response.stats) {
          setStats(response.stats);
        }
      }
    } catch (err) {
      setError(err.message);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar estadísticas
  const fetchStats = useCallback(async (searchFilters = {}) => {
    try {
      const params = { ...filters, ...searchFilters };
      const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value && value.toString().trim() !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await cashMovementsService.getCashMovementsStats(cleanParams);
      
      // El backend ahora devuelve { success: true, stats: {...} }
      if (response.success && response.stats) {
        setStats(response.stats);
      } else if (response.stats) {
        // Fallback para compatibilidad
        setStats(response.stats);
      } else {
        // Fallback para respuesta directa
        setStats(response);
      }
      
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [filters]);

  // Aplicar filtros
  const applyCashMovementsFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Aplicar filtros ahora (búsqueda) - solo cuando se presiona "Buscar"
  const applyFiltersNow = useCallback(() => {
    
    // Marcar que las estadísticas están activas (filtros aplicados)
    setStatsActive(true);
    
    if (filters.startDate || filters.endDate || filters.sede) {
      // Si hay filtros de fecha/sede, usar searchByDateRange que incluye stats
      searchByDateRange(filters.startDate, filters.endDate, filters.sede);
    } else {
      // Si no hay filtros, cargar movimientos y stats por separado
      fetchCashMovements();
      fetchStats();
    }
  }, [filters, searchByDateRange, fetchCashMovements, fetchStats]);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      sede: ''
    });
    
    // Limpiar estadísticas cuando se limpien los filtros
    setStats({
      totalMovements: 0,
      totalIncome: 0,
      totalExpenses: 0,
      netBalance: 0
    });
    
    // Marcar estadísticas como inactivas
    setStatsActive(false);
    
    // Cargar datos iniciales sin filtros
    fetchCashMovements();
  }, [fetchCashMovements]);

  // Crear movimiento
  const createMovement = useCallback(async (movementData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newMovement = await cashMovementsService.createCashMovement(movementData);
      setMovements(prev => [newMovement, ...prev]);
      await fetchStats();
      return newMovement;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  // Actualizar movimiento
  const updateMovement = useCallback(async (id, movementData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedMovement = await cashMovementsService.updateCashMovement(id, movementData);
      setMovements(prev => 
        prev.map(movement => 
          movement.id === id ? updatedMovement : movement
        )
      );
      await fetchStats();
      return updatedMovement;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  // Eliminar movimiento
  const deleteMovement = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await cashMovementsService.deleteCashMovement(id);
      setMovements(prev => prev.filter(movement => movement.id !== id));
      await fetchStats();
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  // Exportar movimientos a Excel
  const exportMovements = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { ...filters };
      const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value && value.toString().trim() !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const blob = await cashMovementsService.exportCashMovements(cleanParams);
      
      // Crear y descargar archivo
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `movimientos-caja-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Cargar datos iniciales solo al montar el componente
  useEffect(() => {
    // Solo cargar datos iniciales, no estadísticas hasta que se busque
    fetchCashMovements();
  }, [fetchCashMovements]);

  return {
    // Estados
    movements,
    loading,
    error,
    filters,
    stats,
    statsActive,

    // Métodos de búsqueda
    fetchCashMovements,
    searchByDateRange,
    fetchStats,

    // Métodos de filtros
    applyCashMovementsFilters,
    applyFiltersNow,
    clearFilters,

    // Métodos CRUD
    createMovement,
    updateMovement,
    deleteMovement,

    // Utilidades
    exportMovements,
    clearError
  };
};